import { apiFetch, ApiError } from '../config/api';
import { notifyUnauthorized } from './apiAuth';

/** Extrait le nom de fichier d'un en-tête Content-Disposition, s'il y en a un. */
export function nomDepuisContentDisposition(valeur) {
  if (!valeur) return null;
  const etendu = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(valeur);
  if (etendu) {
    try {
      return decodeURIComponent(etendu[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* encodage invalide : on tente la forme simple */
    }
  }
  const simple = /filename\s*=\s*("?)([^";]+)\1/i.exec(valeur);
  return simple ? simple[2].trim() : null;
}

/**
 * Télécharge un fichier PROTÉGÉ de l'API et déclenche son enregistrement.
 *
 * Un lien `<a href download>` ou un `fetch(url)` brut part sans en-tête
 * `Authorization` : sous authentification, le navigateur enregistrait une page
 * d'erreur 401 à la place du livre. On passe donc par `apiFetch`, on contrôle
 * `response.ok`, puis on enregistre le blob via un lien temporaire.
 *
 * Lève une `ApiError` en cas d'échec ; à l'appelant de la signaler
 * (`handleApiError`, toast…).
 *
 * @param {string} endpoint - chemin API (sans préfixe /api)
 * @param {string} nomParDefaut - nom proposé si le serveur n'en donne pas
 * @returns {Promise<string>} le nom de fichier proposé à l'enregistrement
 */
export async function telechargerFichierApi(endpoint, nomParDefaut = 'fichier') {
  const reponse = await apiFetch(endpoint);
  if (!reponse.ok) {
    if (reponse.status === 401) notifyUnauthorized();
    let message = `HTTP ${reponse.status}`;
    try {
      const corps = await reponse.json();
      message =
        corps?.error?.message ||
        corps?.detail ||
        (typeof corps?.error === 'string' ? corps.error : null) ||
        message;
    } catch {
      /* corps non JSON : le statut suffit */
    }
    throw new ApiError(message, reponse.status, endpoint);
  }

  const blob = await reponse.blob();
  const nom =
    nomDepuisContentDisposition(reponse.headers?.get?.('Content-Disposition')) || nomParDefaut;

  const urlObjet = window.URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = urlObjet;
  lien.download = nom;
  lien.style.display = 'none';
  document.body.appendChild(lien);
  try {
    lien.click();
  } finally {
    document.body.removeChild(lien);
    // Révocation différée : certains navigateurs lisent l'URL après le clic.
    setTimeout(() => window.URL.revokeObjectURL(urlObjet), 0);
  }
  return nom;
}
